/**
 * MFA Settings Component
 *
 * Allows users to manage their MFA settings.
 * Shows current MFA status and allows enabling/disabling.
 *
 * Usage:
 * Add to user profile page or settings page
 *
 * Part of Phase 3: TOTP MFA for Staff Accounts
 */

import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNotify } from 'react-admin';
import { MfaSetup } from './MfaSetup';
import config from '../config';
import { decodeToken, getStoredToken } from '../services/tokenRefreshService';
import Logger from '../utils/Logger';

export const MfaSettings = () => {
  const notify = useNotify();
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [disabling, setDisabling] = useState(false);

  useEffect(() => {
    // Check if user has MFA enabled by decoding their token
    const token = getStoredToken();
    if (token) {
      const decoded = decodeToken(token);
      // In a real implementation, you'd fetch user profile from backend
      // For now, we'll assume MFA status is in the token or need to fetch it
      setMfaEnabled(false); // Default to false, should be fetched from backend
    }
    setLoading(false);
  }, []);

  const handleEnableMfa = () => {
    setSetupDialogOpen(true);
  };

  const handleSetupSuccess = () => {
    setMfaEnabled(true);
    notify('MFA enabled successfully', { type: 'success' });
  };

  const handleDisableMfa = async () => {
    if (!window.confirm('Are you sure you want to disable MFA? This will make your account less secure.')) {
      return;
    }

    setDisabling(true);

    try {
      const token = getStoredToken();
      const response = await fetch(`${config.API_URL}/auth/mfa`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to disable MFA');
      }

      setMfaEnabled(false);
      notify('MFA disabled successfully', { type: 'success' });
    } catch (error) {
      Logger.error('[MFA Settings] Disable error:', error);
      notify('Failed to disable MFA', { type: 'error' });
    } finally {
      setDisabling(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ShieldIcon sx={{ mr: 1, color: mfaEnabled ? 'success.main' : 'text.secondary' }} />
            <Typography variant="h6">Two-Factor Authentication</Typography>
            {mfaEnabled && (
              <Chip
                icon={<CheckCircleIcon />}
                label="Enabled"
                color="success"
                size="small"
                sx={{ ml: 2 }}
              />
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" paragraph>
            Add an extra layer of security to your account by requiring a verification code from
            your authenticator app when signing in.
          </Typography>

          {mfaEnabled ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Your account is protected with two-factor authentication.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Two-factor authentication is not enabled. Enable it to improve your account security.
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            {mfaEnabled ? (
              <Button
                variant="outlined"
                color="error"
                onClick={handleDisableMfa}
                disabled={disabling}
                startIcon={disabling && <CircularProgress size={20} />}
              >
                {disabling ? 'Disabling...' : 'Disable MFA'}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleEnableMfa}
                startIcon={<ShieldIcon />}
              >
                Enable MFA
              </Button>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            Supported apps: Google Authenticator, Microsoft Authenticator, Authy
          </Typography>
        </CardContent>
      </Card>

      <MfaSetup
        open={setupDialogOpen}
        onClose={() => setSetupDialogOpen(false)}
        onSuccess={handleSetupSuccess}
      />
    </>
  );
};

export default MfaSettings;
